import styled, { createGlobalStyle } from 'styled-components'
import { Input, Radio } from 'antd'
import {
  black,
  chalkboard600,
  cream300,
  fadedBlack,
  fadedBlack2,
  inputBorder,
  mediumDesktopExactWidth,
  red,
  white,
} from '@edulastic/colors'
import IconCloseCircle from '@edulastic/icons/src/IconCloseCircle'
import CustomModalStyled from '../CustomModalStyled'

export const FEEDBACK_MODAL_BORDER_COLOR = '#596F07'

const FEEDBACK_RADIO_LABEL_COLOR = '#000'

const FEEDBACK_TEXTAREA_BG = '#FAFAFA'

export const FEEDBACK_MODAL_WRAP_CLASS = 'feedback-modal-wrap'

export const FeedbackModalGlobalStyle = createGlobalStyle`

  .ant-modal-wrap.${FEEDBACK_MODAL_WRAP_CLASS} {
    z-index: 1100;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 0 24px;
    overflow: visible;
    pointer-events: none;
  }

  .ant-modal-wrap.${FEEDBACK_MODAL_WRAP_CLASS} .ant-modal {
    top: 0 !important;
    padding-bottom: 0;
    margin: 0;
    flex: 0 0 auto;
  }
`

export const FeedbackCustomModal = styled(CustomModalStyled)`
  top: 0;
  padding-bottom: 0;
  margin: 0;
  .ant-modal-content {
    border: 1px solid ${FEEDBACK_MODAL_BORDER_COLOR};
    border-radius: 12px 12px 0 0;
    box-shadow: 0px 0px 17px rgba(0, 0, 0, 0.14);
    padding: 0;
    overflow: hidden;
    height: fit-content;
    min-height: 414px;

    .ant-modal-close {
      top: 26px;
      right: 24px;
      width: 22px;
      height: 22px;
      line-height: 1;
      color: #000;

      .ant-modal-close-x {
        width: 22px;
        height: 22px;
        line-height: 22px;
      }

      svg {
        width: 22px;
        height: 22px;
      }
    }

    .ant-modal-header {
      padding: 24px;
      border-bottom: 1px solid ${cream300};
      margin-bottom: 0;

      .ant-modal-title {
        line-height: 26px;
        color: #000;

        @media (min-width: ${mediumDesktopExactWidth}) {
          font-size: 20px;
        }
      }
    }

    .ant-modal-body {
      padding: ${({ $bodyPadding }) => $bodyPadding || '24px'};
    }

    .ant-modal-footer {
      padding: 0;
      margin: 0;
      border-top: 1px solid ${cream300};
    }
  }
`

export const FeedbackFooterContent = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 24px;
`

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const FeedbackTypeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const SectionLabel = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #000;
`

export const FeedbackRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

export const FeedbackRadio = styled(Radio)`
  display: flex;
  align-items: center;
  margin: 0;
  line-height: 20px;

  &.ant-radio-wrapper {
    .ant-radio {
      top: 0;

      .ant-radio-inner {
        width: 24px;
        height: 24px;
        border-color: ${FEEDBACK_RADIO_LABEL_COLOR};
        background: ${white};

        &::after {
          width: 12px;
          height: 12px;
          top: 50%;
          left: 50%;
          margin: 0;
          transform: translate(-50%, -50%) scale(0);
        }
      }

      &.ant-radio-checked {
        .ant-radio-inner {
          border-color: ${chalkboard600};
          background: ${white};

          &::after {
            background-color: ${chalkboard600};
            transform: translate(-50%, -50%) scale(1);
          }
        }
      }

      .ant-radio-input:focus + .ant-radio-inner {
        box-shadow: none;
      }
    }

    .ant-radio + span {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: ${FEEDBACK_RADIO_LABEL_COLOR};
      text-transform: none;
      padding-left: 8px;
      padding-right: 0;
    }
  }
`

export const FeedbackTextArea = styled(Input.TextArea)`
  &.ant-input {
    background: ${FEEDBACK_TEXTAREA_BG};
    border: 1px solid ${inputBorder};
    border-radius: 6px;
    min-height: 199px;
    padding: 12px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${black};
    resize: none;

    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
      font-weight: 600;
    }

    &:focus,
    &:hover {
      border: 1px solid ${inputBorder} !important;
      background: ${FEEDBACK_TEXTAREA_BG};
      box-shadow: none;
    }
  }
`

export const SubmitButton = styled.button`
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: ${chalkboard600};
  color: ${white};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-transform: uppercase;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    filter: brightness(0.95);
    outline: none;
  }
`

export const WordCountText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ $exceeded }) => ($exceeded ? red : fadedBlack2)};
  text-align: right;
`

export const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 270px;
  padding: 24px;
`

export const SuccessIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
`

export const SuccessMessage = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  color: ${fadedBlack};
`
export const FailureContainer = SuccessContainer

export const FailureIconWrapper = SuccessIconWrapper

export const FailureIcon = styled(IconCloseCircle)`
  width: 48px;
  height: 48px;
  fill: ${red};
`

export const FailureMessage = SuccessMessage

export const SubmitAnotherButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: ${chalkboard600};
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    outline: none;
  }
`

export const DiscardConfirmModal = styled(CustomModalStyled)`
  .ant-modal-content {
    border-radius: 4px;
    box-shadow: 0px 0px 17px rgba(0, 0, 0, 0.14);
    padding: 0;
    overflow: hidden;

    .ant-modal-close {
      top: 24px;
      right: 24px;
      width: 22px;
      height: 22px;
      line-height: 1;
      color: #000;

      .ant-modal-close-x {
        width: 22px;
        height: 22px;
        line-height: 22px;
      }

      svg {
        width: 22px;
        height: 22px;
      }
    }

    .ant-modal-header {
      padding: 24px 24px 0;
      border-bottom: none;
      margin-bottom: 0;

      .ant-modal-title {
        line-height: 26px;
        color: #000;
        font-size: 20px;
        font-weight: 600;
      }
    }

    .ant-modal-body {
      padding: 24px 24px 0px;
    }

    .ant-modal-footer {
      padding: 0;
      margin: 0;
      border-top: none;
    }
  }
`

export const DiscardConfirmMessage = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${fadedBlack};
`

export const DiscardConfirmFooter = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 24px;
`

const discardConfirmButtonStyles = `
  flex: 1;
  height: 40px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-transform: uppercase;
  cursor: pointer;

  &:focus-visible {
    outline: none;
  }
`

export const KeepEditingButton = styled.button`
  ${discardConfirmButtonStyles}
  border: 1px solid ${chalkboard600};
  background: ${white};
  color: ${chalkboard600};

  &:hover {
    background: rgba(89, 111, 7, 0.05);
  }
`

export const DiscardButton = styled.button`
  ${discardConfirmButtonStyles}
  border: none;
  background: ${red};
  color: ${white};

  &:hover {
    filter: brightness(0.95);
  }
`
